import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useDateFormatter } from '../../../../hooks/useDateFormatter';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface TrendDataPoint {
  date: string;
  cases?: number;
  incidents: number;
  serviceRequests: number;
  changes: number;
  problems: number;
}

interface TrendLineChartProps {
  data: TrendDataPoint[];
  onPointClick?: (date: string) => void;
}

const TrendLineChart: React.FC<TrendLineChartProps> = ({ data, onPointClick }) => {
  const { formatDate } = useDateFormatter();

  // Format date labels
  const labels = data.map(d => formatDate(d.date));

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Cases',
        data: data.map(d => d.cases || 0),
        borderColor: 'rgb(147, 51, 234)',
        backgroundColor: 'rgba(147, 51, 234, 0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: 'Incidents',
        data: data.map(d => d.incidents),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: 'Service Requests',
        data: data.map(d => d.serviceRequests),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: 'Changes',
        data: data.map(d => d.changes),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: 'Problems',
        data: data.map(d => d.problems),
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 15,
          usePointStyle: true,
          font: { size: 12 }
        }
      },
      tooltip: {
        callbacks: {
          title: (items: any[]) => {
            if (items.length > 0) {
              const index = items[0].dataIndex;
              return data[index]?.date || '';
            }
            return '';
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { precision: 0 }
      }
    },
  };

  // Handle click anywhere on the chart container
  const handleChartContainerClick = () => {
    if (onPointClick) {
      onPointClick(''); // Empty string indicates show trend summary, not a specific date
    }
  };

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Trend Over Time</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No trend data available for the selected period
        </div>
      </div>
    );
  }

  return (
    <div id="chart-trend-line" className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Trend Over Time</h3>
        <span className="text-xs text-gray-500">Click chart to view details</span>
      </div>
      <div
        className="h-64 cursor-pointer"
        onClick={handleChartContainerClick}
      >
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default TrendLineChart;
